import {
  IconArrowBackUp,
  IconChevronDown,
  IconPaperclip,
} from '@tabler/icons-react';
import React from 'react';
import { Avatar } from 'src/design/atoms/Avatar';
import { Box } from 'src/design/atoms/Box';
import { Center } from 'src/design/atoms/Center';
import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Accordion } from 'src/design/molecules/Accordion';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { EmailAttachmentDto, EmailDto } from 'src/api/FiretrolApiClient';
import { useRecordEmails } from 'src/api/queries/emails/useEmails';
import { useDownloadEmailAttachment } from 'src/api/queries/emails/useDownloadEmailAttachment';
import { formatDateTime } from 'src/utils';
export interface IEmailProps {
  jobNumber: string;
  productType: string;
  productNumber: string;
}

const groupByConversation = (
  emails: EmailDto[],
): Record<string, EmailDto[]> => {
  const groups: Record<string, EmailDto[]> = {};
  emails.forEach((email) => {
    const key = email.conversationId || String(email.technicalKey);
    if (!groups[key]) groups[key] = [];
    groups[key].push(email);
  });
  return groups;
};

export const Email: React.FC<IEmailProps> = ({
  jobNumber,
  productType,
  productNumber,
}) => {
  const { data, isLoading: loading } = useRecordEmails(
    jobNumber,
    productType,
    productNumber,
  );
  const emails: EmailDto[] = data ?? [];
  const downloadEmailAttachment = useDownloadEmailAttachment();
  const onReplyClick = (email: EmailDto) => {
    const ccEmails = (email.toRecipients ?? [])
      .map((r) => r.recipient?.address)
      .filter((addr): addr is string => Boolean(addr))
      .join(';');
    const url = `mailto:${email.sender?.address ?? ''}?subject=Re: ${
      email.subject ?? ''
    }&cc=${ccEmails}&body=%0D%0A%0D%0A---------%0D%0A%0D%0A${
      email.bodyPreview ?? ''
    }`;
    window.open(url, '_blank');
  };

  const downloadAttachment = async (attachment: EmailAttachmentDto) => {
    const result = await downloadEmailAttachment
      .mutateAsync(Number(attachment.technicalKey))
      .catch(() => null);
    if (result) window.open(result.url, '_blank');
  };

  const renderAttachments = (attachments: EmailAttachmentDto[] | undefined) => {
    return (attachments ?? []).map((att) => {
      return (
        <div
          style={{ paddingLeft: 25, paddingBottom: 15 }}
          key={att.technicalKey}
        >
          <a onClick={() => downloadAttachment(att)}>{att.name}</a>
        </div>
      );
    });
  };
  const renderEmails = () => {
    const grouped = groupByConversation(emails);
    return Object.keys(grouped).map((key) => {
      const thread = grouped[key];
      const firstEmail = thread[0];
      if (!firstEmail) return null;
      const hasAttachment = thread.some(
        (e) => (e.attachments?.length ?? 0) > 0,
      );
      return (
        <Accordion key={key}>
          <Accordion.Item value={key}>
            <Accordion.Control>
              <Text fw={700} component="span">
                ({formatDateTime(firstEmail.sentDatetime, 'MM/DD/YYYY h:mma')}){' '}
                {firstEmail.sender?.name} - {firstEmail.subject}
                {hasAttachment && (
                  <IconPaperclip
                    style={{ marginLeft: '10px', fontSize: '16px' }}
                  />
                )}
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              {thread.map((email) => {
                return (
                  <React.Fragment key={email.technicalKey}>
                    <Group align="flex-start" wrap="nowrap" py="sm">
                      <Avatar alt={email.sender?.name}>
                        {email.sender?.name?.slice(0, 1) ?? ''}
                      </Avatar>
                      <Stack gap={0} style={{ flex: 1 }}>
                        <Text fw={500}>
                          {email.sender?.name} - {email.subject}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {formatDateTime(
                            email.sentDatetime,
                            'MM/DD/YYYY h:mma',
                          )}
                          &nbsp;- {email.bodyPreview}
                        </Text>
                      </Stack>
                      <BasicActionIcon
                        variant="subtle"
                        aria-label="reply"
                        onClick={() => onReplyClick(email)}
                      >
                        <IconArrowBackUp />
                      </BasicActionIcon>
                    </Group>
                    {renderAttachments(email.attachments)}
                    <Divider />
                  </React.Fragment>
                );
              })}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      );
    });
  };
  return (
    <>
      {loading && (
        <Center>
          <Loader />
        </Center>
      )}
      {!loading && emails.length === 0 && (
        <Center>
          <Text>No emails found</Text>
        </Center>
      )}
      {!loading && emails.length > 0 && renderEmails()}
    </>
  );
};
